/**
 * Feishu/Lark helpers
 *
 * 目标：
 * - 判断是否在飞书客户端内打开
 * - 生成飞书 OAuth 授权地址（用于“飞书内免登”跳转）
 */
export function isInFeishuClient(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  return /feishu|lark/i.test(ua);
}

export function getFeishuAppId(): string {
  // 兼容历史命名：VITE_OIDC_APPID
  const env: any = (import.meta as any).env || {};
  return env.VITE_OIDC_APPID || env.VITE_FEISHU_APP_ID || '';
}

/**
 * 飞书 OAuth 回调地址
 *
 * 注意：飞书开放平台通常要求 redirect_uri 与后台配置完全一致（包含协议/域名/端口/路径）。
 * 你现有扫码流程使用的是 `fsCallback.html`（public 静态页），因此默认也用它。
 *
 * 如果你希望用环境变量覆盖，建议把 VITE_FEISHU_REDIRECT_URI 配置成包含 `fsCallback` 的完整地址；
 * 若 env 配置为其它路径（例如 `/callback`），这里会回退到默认以避免破坏现有扫码链路。
 */
export function getFeishuRedirectUri(): string {
  const env: any = (import.meta as any).env || {};
  const fromEnv: string | undefined = env.VITE_FEISHU_REDIRECT_URI;
  if (fromEnv && /fsCallback(\.html)?/i.test(fromEnv)) {
    return fromEnv.replace(/\/$/, '');
  }

  // 本地开发时页面 origin 多为 localhost，但飞书后台登记的 redirect_uri 通常是测试/预发域名，
  // 此时与 VITE_APP_BASE_URL（测试环境前端根地址）不一致，应使用配置的测试回调，否则会报 4401。
  const baseUrl = (env.VITE_APP_BASE_URL || '').replace(/\/$/, '');
  if (baseUrl && /^https?:\/\//i.test(baseUrl) && typeof window !== 'undefined') {
    const pageOrigin = window.location.origin;
    if (pageOrigin && baseUrl !== pageOrigin) {
      return `${baseUrl}/fsCallback.html`;
    }
  }

  return `${window.location.origin}/fsCallback.html`;
}

export function buildFeishuAuthorizeUrl(params?: {
  appId?: string;
  redirectUri?: string;
  state?: string;
}): string {
  const APPID = params?.appId || getFeishuAppId();
  const STATE = encodeURIComponent(params?.state || 'success_login');
  const redirect_uri = params?.redirectUri || getFeishuRedirectUri();
  const authURL = `https://open.feishu.cn/open-apis/authen/v1/index?redirect_uri=${encodeURIComponent(redirect_uri)}&app_id=${APPID}&state=${STATE}`;
  return authURL;
}

